import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiTrendingUp, FiToggleLeft, FiToggleRight, FiDollarSign, FiUsers, FiShare2, FiBarChart3 } = FiIcons;

const Marketing = () => {
  const [communityPromoEnabled, setCommunityPromoEnabled] = useState(true);
  const [monthlyBudget, setMonthlyBudget] = useState('150');

  const marketingStats = [
    { label: 'Local Campaign Reach', value: '2,340', change: '+18%', icon: FiUsers },
    { label: 'Generated Leads', value: '45', change: '+12%', icon: FiTrendingUp },
    { label: 'Referrals This Month', value: '8', change: '+3', icon: FiShare2 },
    { label: 'Ad Spend', value: '$142', change: '-$8', icon: FiDollarSign },
  ];

  const referralData = [
    { name: 'Sara K.', referred: 'Mike Chen', service: 'Cleaning', date: '2024-01-10', status: 'Booked' },
    { name: 'Lisa R.', referred: 'Emma Wilson', service: 'Pet Sitting', date: '2024-01-08', status: 'Pending' },
    { name: 'David W.', referred: 'Tom Johnson', service: 'Tutoring', date: '2024-01-05', status: 'Booked' },
  ];

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <motion.div 
        className="flex items-center justify-between"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Marketing</h1>
          <p className="text-slate-600 mt-1">Grow your local visibility</p>
        </div>
      </motion.div>

      {/* Marketing Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {marketingStats.map((stat, index) => (
          <motion.div
            key={stat.label}
            className="bg-white rounded-xl p-6 shadow-sm border border-slate-200"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg bg-trust-100">
                <SafeIcon icon={stat.icon} className="text-xl text-trust-600" />
              </div>
              <span className={`text-sm font-medium ${stat.change.startsWith('+') ? 'text-green-600' : 'text-red-600'}`}>
                {stat.change}
              </span>
            </div>
            <div className="text-2xl font-bold text-slate-800 mb-1">{stat.value}</div>
            <div className="text-slate-600 text-sm">{stat.label}</div>
          </motion.div>
        ))}
      </div>

      {/* Community Promo Settings */}
      <motion.div 
        className="bg-white rounded-xl p-6 border border-slate-200"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.5 }}
      >
        <h2 className="text-xl font-bold text-slate-800 mb-4">Community Promotion</h2>
        
        <div className="flex items-center justify-between p-4 bg-gradient-to-r from-trust-50 to-olive-50 rounded-lg mb-6">
          <div>
            <h3 className="font-semibold text-slate-800">Feature me in local community campaigns</h3>
            <p className="text-slate-600 text-sm">Get featured in neighborhood newsletters and local business directories</p>
          </div>
          <motion.button
            onClick={() => setCommunityPromoEnabled(!communityPromoEnabled)}
            className="flex items-center"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <SafeIcon 
              icon={communityPromoEnabled ? FiToggleRight : FiToggleLeft} 
              className={`text-4xl ${communityPromoEnabled ? 'text-trust-500' : 'text-slate-400'}`}
            />
          </motion.button>
        </div>

        {/* Budget Input */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Monthly Marketing Budget
          </label>
          <div className="relative">
            <SafeIcon icon={FiDollarSign} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
            <input
              type="number"
              value={monthlyBudget}
              onChange={(e) => setMonthlyBudget(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-trust-500"
              placeholder="Enter amount"
            />
          </div>
          <p className="text-slate-500 text-sm mt-1">
            Recommended: $100-$300 for optimal local reach
          </p>
        </div>

        {/* QR Code Generator */}
        <div className="p-4 bg-slate-50 rounded-lg">
          <h3 className="font-semibold text-slate-800 mb-2">QR Code for Flyers</h3>
          <div className="flex items-center gap-4">
            <div className="w-24 h-24 bg-white border-2 border-slate-200 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiBarChart3} className="text-2xl text-slate-400" />
            </div>
            <div>
              <p className="text-slate-600 text-sm mb-2">
                Generate a QR code linking to your services for print materials
              </p>
              <motion.button
                className="bg-trust-500 text-white px-4 py-2 rounded-lg hover:bg-trust-600 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Generate QR Code
              </motion.button>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Referral Tracking */}
      <motion.div 
        className="bg-white rounded-xl p-6 border border-slate-200"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.6 }}
      >
        <h2 className="text-xl font-bold text-slate-800 mb-4">Referral Tracking</h2>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left p-3 font-semibold text-slate-700">Referrer</th>
                <th className="text-left p-3 font-semibold text-slate-700">Referred Client</th>
                <th className="text-left p-3 font-semibold text-slate-700">Service</th>
                <th className="text-left p-3 font-semibold text-slate-700">Date</th>
                <th className="text-left p-3 font-semibold text-slate-700">Status</th>
              </tr>
            </thead>
            <tbody>
              {referralData.map((referral, index) => (
                <motion.tr
                  key={index}
                  className="border-b border-slate-100 hover:bg-slate-50"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <td className="p-3 font-medium text-slate-800">{referral.name}</td>
                  <td className="p-3 text-slate-600">{referral.referred}</td>
                  <td className="p-3">
                    <span className="bg-trust-100 text-trust-800 px-2 py-1 rounded-full text-xs font-medium">
                      {referral.service}
                    </span>
                  </td>
                  <td className="p-3 text-slate-600">{referral.date}</td>
                  <td className="p-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      referral.status === 'Booked' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {referral.status}
                    </span>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );
};

export default Marketing;